import { create } from 'zustand';
import type { EffectivePermissions, User } from '@/types/auth';
import { getMe, getMyCapabilities, login as apiLogin } from '@/api/auth';
import { useSessionStore } from './useSessionStore';
import { useChatStore } from './useChatStore';
import { useConfigStore } from './useConfigStore';

function clearPrivateState() {
  useSessionStore.getState().reset();
  useChatStore.getState().clearMessages();
  useConfigStore.setState(useConfigStore.getInitialState());
}

interface AuthState {
  token: string | null;
  user: User | null;
  permissions: EffectivePermissions | null;
  actions: Record<string, boolean>;
  scope: 'own' | 'all';
  permissionVersion: string | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;
}

interface AuthActions {
  init: () => Promise<void>;
  refreshCapabilities: () => Promise<void>;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  setError: (error: string | null) => void;
}

const storedToken = localStorage.getItem('deepflow_token');

export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  token: storedToken,
  user: null,
  permissions: null,
  actions: {},
  scope: 'own',
  permissionVersion: null,
  loading: false,
  initialized: false,
  error: null,

  init: async () => {
    const token = get().token;
    if (!token) {
      set({ initialized: true });
      return;
    }
    set({ loading: true, error: null });
    try {
      const [user, capabilityData] = await Promise.all([getMe(), getMyCapabilities()]);
      if (get().token !== token) return;
      set({ user, permissions: capabilityData.permissions, actions: capabilityData.actions,
        scope: capabilityData.scope, permissionVersion: capabilityData.permission_version,
        loading: false, initialized: true });
    } catch {
      if (get().token !== token) return;
      localStorage.removeItem('deepflow_token');
      get().logout();
      set({ loading: false, initialized: true });
    }
  },

  refreshCapabilities: async () => {
    const token = get().token;
    if (!token) return;
    try {
      const data = await getMyCapabilities();
      if (get().token !== token) return;
      if (get().permissionVersion !== data.permission_version) clearPrivateState();
      set({ permissions: data.permissions, actions: data.actions, scope: data.scope,
        user: get().user ? { ...get().user!, role: data.role || get().user!.role } : null,
        permissionVersion: data.permission_version });
    } catch {
      // API guards still enforce the current permission on every request.
    }
  },

  login: async (username: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const data = await apiLogin(username, password);
      localStorage.setItem('deepflow_token', data.access_token);
      const capabilityData = await getMyCapabilities();
      clearPrivateState();
      set({ token: data.access_token, user: data.user, permissions: capabilityData.permissions,
        actions: capabilityData.actions, scope: capabilityData.scope,
        permissionVersion: capabilityData.permission_version, loading: false, initialized: true });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '登录失败';
      localStorage.removeItem('deepflow_token');
      set({ token: null, user: null, permissions: null, error: message, loading: false, initialized: true });
      return false;
    }
  },

  logout: () => {
    localStorage.removeItem('deepflow_token');
    clearPrivateState();
    set({ token: null, user: null, permissions: null, actions: {}, scope: 'own', permissionVersion: null, error: null });
  },

  setError: (error: string | null) => set({ error }),
}));
